<script setup lang="ts">
import type { FileDetail, VideoDetail } from '~/types/api'

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute('news-slug___cs')
const { slug } = route.params
const { trimDescription, addSuffix } = useMeta()

const galleryImages = ref<FileDetail[]>([])
const videos = ref<VideoDetail[]>([])
const releaseDate = ref<string>()

const newsRepository = createNewsRepository()

// Load news data by slug
const {
  data: newsDetail,
  error,
  status,
} = await useLazyAsyncData(() => newsRepository.getBySlug(slug))

async function checkTranslatedSlug() {
  const translatedSlug = await newsRepository.translateSlug(slug)

  if (translatedSlug) {
    return await navigateTo(
      localePath({
        name: 'news-slug',
        params: { slug: translatedSlug.urlSlug },
      })
    )
  } else {
    throw createError({ statusCode: 404, message: t('error.pageNotFound') })
  }
}

if (error.value) {
  await checkTranslatedSlug()
}

watchEffect(async () => {
  if (newsDetail.value) {
    releaseDate.value = new Intl.DateTimeFormat('cs-CZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(newsDetail?.value?.releaseDate || ''))

    galleryImages.value = newsDetail.value?.galleryImages ?? []
    videos.value = newsDetail.value?.videos?.filter((v) => v.videoId) ?? []
  } else if (status.value !== 'pending') {
    await checkTranslatedSlug()
  }
})

if (newsDetail.value?.translationOfNid) {
  await useTranslatedSlugs(newsDetail.value?.translationOfNid)
}

const { defaultOgImage } = useOgImage()

useSeoMeta({
  title: addSuffix(newsDetail.value?.title) ?? t('pages.news.meta.title'),
  description:
    trimDescription(stripHtml(newsDetail.value?.content)) ??
    t('pages.index.meta.description'),
  ogImage:
    newsDetail.value?.thumbnailImage?.url ??
    newsDetail.value?.galleryImages[0].url ??
    defaultOgImage,
})
</script>

<template>
  <div>
    <div v-if="status === 'pending'">
      <UiSkeletonBanner />
      <UiSkeletonContentBlock />
      <UiSkeletonContentBlock />
    </div>
    <div v-else>
      <main>
        <UiBanner
          :title="newsDetail?.title ?? ''"
          :slug="newsDetail?.title ?? ''"
        />
        <div class="container">
          <TypographyBaseBold class="mx-auto mb-2 max-w-3xl">
            {{ releaseDate }}
          </TypographyBaseBold>
        </div>
        <section class="container">
          <div>
            <div class="mx-auto mb-20 flex max-w-3xl flex-col gap-10 text-sm">
              <div class="html-news-content" v-html="newsDetail?.content"></div>
            </div>
          </div>
        </section>
        <div v-if="videos.length > 0" class="container">
          <div class="mb-8 text-center">
            <div class="text-xl">{{ $t('base.news.videos') }}</div>
          </div>
          <div class="flex flex-wrap justify-center">
            <div
              v-for="video in videos"
              :key="video.nid"
              class="w-full px-2 md:w-1/2"
              style="aspect-ratio: 16 / 9"
            >
              <UiYoutubePlayer
                :video-id="video.videoId"
                :poster="video.posterImage?.url ?? ''"
              />
            </div>
          </div>
        </div>
        <NewsGallery v-if="galleryImages.length > 0" :images="galleryImages" />
        <!-- TODO: https://projects.jagu.cz/issues/21844 -->
        <NewsContactForm
          v-if="newsDetail?.showForm && false"
          :title="$t('base.news.form.title')"
          :description="$t('base.news.form.description')"
        />
        <HomepageNews
          :title="$t('base.news.latestNews')"
          :is-home-page="false"
          :nid="newsDetail?.nid ?? undefined"
        />
      </main>
    </div>
  </div>
</template>

<style>
.html-news-content p:first-of-type {
  font-weight: bolder;
}
.html-news-content p {
  display: block;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0;
  margin-inline-end: 0;
  margin-bottom: 2rem;
}

.html-news-content h2 {
  font-family: 'Pragmatica Ext';
  font-weight: bolder;
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.html-news-content h3 {
  font-family: 'Pragmatica Ext';
  font-weight: 700;
  line-height: 1;
  font-size: 1.5rem;
}

.html-news-content h4 {
  font-family: 'Pragmatica Ext';
  font-weight: 700;
  line-height: 1;
  font-size: 1.25rem;
}

.html-news-content h5 {
  font-family: 'Pragmatica Ext';
  font-weight: 700;
  line-height: 1;
  font-size: 1.125rem;
}

.html-news-content h6 {
  font-family: 'Pragmatica Ext';
  font-weight: 700;
  line-height: 1;
  font-size: 1.1rem;
}

.html-news-content img {
  margin-bottom: 2rem;
}

.html-news-content ul {
  list-style: none;
  padding-left: 0;
}

.html-news-content ul li {
  position: relative;
  padding-left: 1em;
}

.html-news-content ul li::before {
  content: '\2022';
  position: absolute;
  left: 0;
  font-weight: 400 !important;
}
</style>
